/**
 * ProShip Direct v2 — TAT.
 *
 * The number here becomes the delivery date shown to the buyer, so it is the one setting with a
 * customer-facing consequence: on ProShip's TAT the merchant promises ProShip's transit times on
 * their own contract. Stated on the page rather than left implicit.
 *
 * TAT is not versioned: one per courier, replaced by each upload. Where the merchant has none, or
 * none for a zone, ProShip's TAT for the same courier and service applies, read live, so the table
 * always shows what applies and marks whose each zone's days are.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Alert, Chip, Divider, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import withLayout from '../../../hocs/Layout';
import SetupPageShell from '../components/SetupPageShell';
import FileDropZone from '../components/FileDropZone';
import Loader from '../../../common/Loader';
import { SETUP_DIMENSIONS } from '../data/defaults';
import { useCourierTat } from '../data/hooks';
import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { tHeadSx, tCellSx } from '../components/stats';
import { SUBTLE, NAVY, SUCCESS, BORDER, WARNING, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { downloadCsv } from '../data/serviceabilityUpload';
import { MONO } from '../components/directCellStyles';

const dimension = SETUP_DIMENSIONS.find((d) => d.key === 'tat');

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const chipSx = (color) => ({
  fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, height: 20, bgcolor: `${color}14`, color,
});

const HEADER = 'zone,Min Tat days,Max Tat Days';
const TEMPLATE = [HEADER, '1,1,2', '2,2,3', '3,2,5', '4,5,7', '5,7,9', ''].join('\n');

function TatPanel({ account }) {
  const { status, loading, error, upload, switchToDefault } = useCourierTat(account?.accountCode);
  const [staged, setStaged] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  if (loading) return <Loader />;

  const rows = status?.rows || [];
  const own = status?.source === 'OWN';
  const anyDefault = rows.some((r) => r.fromDefault);

  const run = async (fn) => {
    setBusy(true);
    setProblem(null);
    try {
      await fn();
      setStaged(null);
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  const currentCsv = () =>
    [HEADER, ...rows.map((r) => `${r.zone},${r.minTat},${r.maxTat}`), ''].join('\n');

  return (
    <>
      {error && <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>{error}</Alert>}

      {/* ── What applies now ── */}
      <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={sectionTitleSx}>TAT in use</Typography>
          <Chip size='small' label={own ? (anyDefault ? 'Yours, ProShip’s for the rest' : 'Yours') : 'ProShip’s'} sx={chipSx(SUCCESS)} />
          <Box sx={{ flex: 1 }} />
          {rows.length > 0 && (
            <Button
              size='small'
              sx={secondaryBtnSx}
              startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => downloadCsv(`tat-${account.accountCode}.csv`, currentCsv())}
            >
              Download current
            </Button>
          )}
        </Box>

        {rows.length === 0 ? (
          /*
            Neither the merchant nor ProShip has days for this courier and service. Saying so beats an
            empty table: their buyers would get no delivery date.
          */
          <Alert severity='warning' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            ProShip has no TAT for {account.carrierName} on this service, so this courier has none yet. Upload
            your own below.
          </Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tHeadSx, minWidth: 160 }}>Zone</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 90 }}>Min days</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 90 }}>Max days</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 180 }}>Buyer sees</TableCell>
                  <TableCell sx={{ ...tHeadSx, minWidth: 110 }}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.zone}>
                    <TableCell sx={{ ...tCellSx, minWidth: 160 }}>
                      Zone {r.zone}{r.zoneName ? ` · ${r.zoneName}` : ''}
                    </TableCell>
                    <TableCell sx={{ ...tCellSx, minWidth: 90 }}>{r.minTat}</TableCell>
                    <TableCell sx={{ ...tCellSx, minWidth: 90 }}>{r.maxTat}</TableCell>
                    <TableCell sx={{ ...tCellSx, minWidth: 180 }}>
                      Delivery in {r.minTat === r.maxTat ? r.minTat : `${r.minTat} to ${r.maxTat}`} days
                    </TableCell>
                    <TableCell sx={{ ...tCellSx, minWidth: 110 }}>
                      <Chip size='small' label={r.fromDefault ? 'ProShip’s' : 'Yours'}
                            sx={chipSx(r.fromDefault ? PRIMARY_BLUE : SUCCESS)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Typography sx={{ ...bodySx, mt: 1.25 }}>
          {own
            ? 'The days above are what your buyers are promised. Zones you did not upload use ProShip’s days.'
            : `You are promising ProShip’s transit times for ${account.carrierName} on your own contract. Nothing was copied, so ProShip’s changes reach you automatically.`}
        </Typography>

        {status?.updatedAt && (
          <Typography sx={{ ...smallSx, mt: 1 }}>
            Last changed {formatDate(status.updatedAt, 'dd MMM yyyy, hh:mm a')}
            {status.updatedBy ? ` by ${status.updatedBy}` : ''}
            {own && status.fileName ? ` · ${status.fileName}` : ''}
          </Typography>
        )}
      </Box>

      {/* ── Use ProShip's ── */}
      {own && (
        <Box sx={{ ...sectionCardSx, mb: 1.5 }}>
          <Typography sx={{ ...sectionTitleSx, mb: 0.5 }}>Use ProShip’s TAT instead</Typography>
          <Typography sx={{ ...bodySx, mb: 1.25 }}>
            Removes your TAT from this courier so ProShip’s days for {account.carrierName} apply. Nothing is
            copied, so it stays in step with ProShip’s from then on.
          </Typography>
          <Button
            size='small'
            sx={secondaryBtnSx}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={13} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
            onClick={() => run(switchToDefault)}
          >
            Use ProShip’s TAT
          </Button>
        </Box>
      )}

      {/* ── Upload your own ── */}
      <Box sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 0.75 }}>
          <Typography sx={sectionTitleSx}>Upload your own</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size='small'
            sx={secondaryBtnSx}
            startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => downloadCsv('tat-template.csv', TEMPLATE)}
          >
            Download sample
          </Button>
        </Box>
        <Typography sx={{ ...bodySx, mb: 1.5 }}>
          One row per zone: the zone number, then the minimum and maximum days from pickup to delivery.
          Whole days, 1 to 30, minimum not more than maximum. A zone you leave out keeps ProShip’s days.
          Applies to <Box component='span' sx={{ fontFamily: MONO }}>{account.accountCode}</Box> only, and
          replaces any TAT it had.
        </Typography>

        {staged ? (
          <Box sx={{ border: `1px solid ${SUCCESS}`, borderRadius: RADIUS.md, p: 1.25, mb: 1.5 }}>
            <Typography sx={strongSx}>{staged.name}</Typography>
            <Typography
              onClick={() => setStaged(null)}
              sx={{ ...smallSx, color: WARNING, cursor: 'pointer', textDecoration: 'underline', mt: 0.4 }}
            >
              Remove
            </Typography>
          </Box>
        ) : (
          <FileDropZone onFile={(f) => { setProblem(null); setStaged(f); }} hint='One CSV.' />
        )}

        {/* The server lists every problem with its line number; keep the line breaks. */}
        {problem && (
          <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'], whiteSpace: 'pre-line' }}>
            {problem}
          </Alert>
        )}

        <Divider sx={{ my: 1.5, borderColor: BORDER }} />
        <Button
          size='small'
          variant='contained'
          sx={primaryBtnSx}
          disabled={!staged || busy}
          startIcon={busy ? <CircularProgress size={13} /> : null}
          onClick={() => run(() => upload(staged))}
        >
          {busy ? 'Checking the file…' : 'Upload TAT'}
        </Button>
      </Box>
    </>
  );
}

export function Tat({ embedded = false }) {
  return (
    <SetupPageShell dimension={dimension} embedded={embedded} serverManaged>
      {({ account }) => <TatPanel account={account} />}
    </SetupPageShell>
  );
}

export default withLayout(Tat);
